import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { Box, Fab, Link as MuiLink } from "@mui/material";
import { matchRoutes, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import ChatWarning from "@icons/myicons/chat-warning.svg?react";
import { useSupportLink } from "@hooks/useSupportLink";
import { Tooltip } from "./Tooltip";

const FabStyled = styled(Fab, {
  shouldForwardProp: (prop) => prop !== "position",
})<{ position: "up" | "down" }>`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing(2)};
  right: ${({ theme }) => theme.spacing(2)};
  transition: bottom 0.25s ease;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    bottom: ${({ theme, position }) =>
      theme.spacing(position === "down" ? 2 : 15.5)};
  }
`;

const exceptionRoutes = [
  {
    path: "/web-form/*",
  },
  {
    path: "/import-via-tpp-web-form/*",
  },
  {
    path: "/update-via-tpp-web-form/*",
  },
  {
    path: "/import-bank-connection",
  },
  {
    path: "/update-bank-connection",
  },
  {
    path: "/login",
  },
  {
    path: "/new-transaction-wizard",
  },
  {
    path: "/manual-transaction-wizard",
  },
  {
    path: "/edit-transaction-wizard",
  },
  {
    path: "/add-transaction-wizard",
  },
  {
    path: "/investments/:slug/dividend-wizard",
  },
  {
    path: "/feedback",
  },
];

export const FeedbackButton = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const ref = useRef<HTMLElement | null>(null);
  const [buttonPosition, setButtonPosition] = useState<"up" | "down">("down");
  const supportHref = useSupportLink();

  const checkScrollDirection = useCallback(() => {
    if (
      !ref.current ||
      (ref.current?.style.transform && ref.current?.style.transform !== "none")
    ) {
      setButtonPosition("down");
    } else {
      setButtonPosition("up");
    }
  }, []);

  useLayoutEffect(() => {
    ref.current = document.querySelector("#app-bar");
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", checkScrollDirection);

    return () => {
      window.removeEventListener("scroll", checkScrollDirection);
    };
  }, [checkScrollDirection]);

  const match = matchRoutes(exceptionRoutes, location);
  if (match) return null;

  const FabComponent = (
    <FabStyled position={buttonPosition} color="primary">
      <Box display="flex" color="button.primaryButton.textOnDefault">
        <ChatWarning width="30px" height="30px" />
      </Box>
    </FabStyled>
  );

  return (
    <Tooltip
      text={t("feedbackGlobalButton.tooltip") ?? "Feedback"}
      placement="left"
      isSmall
      arrow
    >
      <MuiLink href={supportHref} target="_blank" rel="noopener noreferrer">
        {FabComponent}
      </MuiLink>
    </Tooltip>
  );
};
